import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { setValidationState } from "reducers/main";
import Card from "components/Card/Card";
import {
  useGetClaimByIdQuery,
  useGetClaimValidationsQuery,
  usePatchClaimByIdMutation,
} from "services/claims-api";
import { useDispatch, useSelector } from "utils/redux";

import { useForm, Controller } from "react-hook-form";
import type { OptionType } from "components/inputs";
import { Select, TextInput } from "components/inputs";
import ClaimsPageContainer from "./ClaimsPageContainer";
import { setCurrentClaim } from "services/claimsDataSlice.ts";

const ClaimantForm = ({
  additionalClaimantIndex,
  constants,
  control,
  getValues,
  errors,
  focusedField,
  handleSave,
}) => {
  return (
    <div key={`claimant-form-${additionalClaimantIndex}`}>
      <div className="grid-layout gl-2 no-gap-tb">
        <div>
          <Card heading="General Information" cardNumber={2}>
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_relationship`}
              render={({ field: { name, value } }) => (
                <Select
                  label="Select Relationship"
                  name={name}
                  errors={errors}
                  isFocused={focusedField === name}
                  options={constants?.RELATIONSHIPS || []}
                  value={constants?.RELATIONSHIPS?.find(
                    (option: OptionType) => option.value === value,
                  )}
                  onChange={(o: OptionType) =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_relationship`,
                      o.value,
                    )
                  }
                  required={true}
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_tin`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  placeholder={"X".repeat(9)}
                  label="Tax ID"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_tin`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_first_name`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="First Name"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_first_name`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_middle_initial`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Middle Initial"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_middle_initial`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_last_name`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Last Name"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_last_name`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
          </Card>
          <Card heading="Address" cardNumber={3}>
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_organization_name`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Company Name"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_organization_name`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_mailing_address_line1`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Address"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_mailing_address_line1`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_mailing_address_line2`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Address Line 2"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_mailing_address_line2`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_city`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="City"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_city`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_state`}
              render={({ field: { name, value } }) => (
                <Select
                  label="Select State"
                  name={name}
                  errors={errors}
                  isFocused={focusedField === name}
                  options={constants?.STATES || []}
                  value={constants?.STATES?.find(
                    (option: OptionType) => option.value === value,
                  )}
                  onChange={(o: OptionType) =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_state`,
                      o.value,
                    )
                  }
                  required={true}
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_zipcode`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Zip Code"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_zipcode`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_zip_plus_4`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Zip Code Extension"
                  placeholder={"X".repeat(4)}
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_zip_plus_4`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_phone`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Phone"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_phone`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_phone_extension`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Phone Extension"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_phone_extension`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
          </Card>
        </div>
        <div>
          <Card heading="Rep Information" cardNumber={4}>
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_indicator`}
              render={({ field: { name, value } }) => (
                <Select
                  label="Select Indicator"
                  name={name}
                  errors={errors}
                  isFocused={focusedField === name}
                  options={constants?.REPRESENTATIVE_TYPES || []}
                  value={constants?.REPRESENTATIVE_TYPES?.find(
                    (option: OptionType) => option.value === value,
                  )}
                  onChange={(o: OptionType) =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_indicator`,
                      o.value,
                    )
                  }
                  required={true}
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_tin`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Tax ID"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_tin`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_first_name`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="First Name"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_first_name`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_last_name`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Last Name"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_last_name`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
          </Card>
          <Card heading="Rep Address" cardNumber={5}>
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_firm_name`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Company Name"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_firm_name`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_mailing_address_line1`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Address"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_mailing_address_line1`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_mailing_address_line2`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Address Line 2"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_mailing_address_line2`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_city`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="City"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_city`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_state`}
              render={({ field: { name, value } }) => (
                <Select
                  label="Select State"
                  name={name}
                  errors={errors}
                  isFocused={focusedField === name}
                  options={constants?.STATES || []}
                  value={constants?.STATES?.find(
                    (option: OptionType) => option.value === value,
                  )}
                  onChange={(o: OptionType) =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_state`,
                      o.value,
                    )
                  }
                  required={true}
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_zipcode`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Zip Code"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_zipcode`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_zip_plus_4`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  placeholder={"X".repeat(4)}
                  label="Zip Code Extension"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_zip_plus_4`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_phone`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Phone"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_phone`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
            <Controller
              control={control}
              name={`claimant_${additionalClaimantIndex}_rep_phone_extension`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Phone Extension"
                  errors={errors}
                  isFocused={focusedField === field.name}
                  onBlur={() =>
                    handleSave(
                      `claimant_${additionalClaimantIndex}_rep_phone_extension`,
                      getValues(field.name),
                    )
                  }
                />
              )}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

const ClaimAdditionalClaimants = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const dispatch = useDispatch();
  const [patchClaimById] = usePatchClaimByIdMutation();
  const [additionalClaimantIndex, setAdditionalClaimantIndex] = useState(null);

  const {
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm(); // Default for claimant selector);

  const { data: claim, isLoading } = useGetClaimByIdQuery(
    { id: claimId },
    { skip: false },
  );

  const { data: claimValidation } = useGetClaimValidationsQuery(
    { id: claimId },
    { skip: !claimId },
  );

  useEffect(() => {
    if (!isLoading) {
      dispatch(setCurrentClaim(claim));
      reset(claim);
    }
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (claim) {
      reset(claim);
    }
  }, [claim, reset]);

  useEffect(() => {
    if (claimValidation) {
      dispatch(
        setValidationState({
          errors: claimValidation.filter((e) => e.level === "ERROR"),
          warnings: claimValidation.filter((e) => e.level === "WARN"),
        }),
      );
    }
  }, [claimValidation, dispatch]);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      try {
        await patchClaimById({
          id: claimId,
          data: field === "" ? value : { [field]: value },
          useDetail: true,
        })
          .unwrap()
          .then((result) => {
            dispatch(setCurrentClaim(result));
          });
      } catch (error) {
        console.error("Save failed:", error);
      }
    },
    [patchClaimById, claimId],
  );

  const claimantOptions = [
    {
      label: "Claimant 1",
      value: "1",
    },
    {
      label: "Claimant 2",
      value: "2",
    },
    {
      label: "Claimant 3",
      value: "3",
    },
    {
      label: "Claimant 4",
      value: "4",
    },
  ];

  const constants = useSelector((state) => state.shared.constants);
  const focusedField = useSelector((state) => state.main.focusedField);

  return (
    <ClaimsPageContainer>
      <Card heading="Select Additional Claimant" cardNumber={1}>
        <Controller
          name="additional_claimant_index"
          control={control}
          render={({ field: { name } }) => (
            <Select
              name={name}
              label="Additional Claimant"
              options={claimantOptions}
              errors={errors}
              onChange={(option: OptionType) => {
                setAdditionalClaimantIndex(option?.value);
              }}
              required={true}
            />
          )}
        />
      </Card>

      {additionalClaimantIndex && (
        <ClaimantForm
          additionalClaimantIndex={additionalClaimantIndex}
          constants={constants}
          control={control}
          getValues={getValues}
          errors={errors}
          focusedField={focusedField}
          handleSave={handleSave}
        />
      )}
    </ClaimsPageContainer>
  );
};

export default ClaimAdditionalClaimants;
